// assets/AllCourses/Exercises/WordJumblesExercise.jsx
"use client";

import React, { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardDescription,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { StringField } from "@/components/shared/FormFields";
import CapitalizationRule from "@/components/shared/CapitalizationRule";
import { cn } from "@/utils/utils";

export default function WordJumblesExercise({
  exercise = {},
  onUpdate,            // Edit mode only
  errors = {},
  isPreview = false,   // false = admin edit, true = student view
  showResult = false,
  onComplete = () => {}, // Student callback
  setAttempt = () => {}, // Progress persistence
}) {
  const isEditMode = typeof onUpdate === "function";

  /* ================= SAFE QUESTIONS ================= */
  const questions = Array.isArray(exercise.wordJumblesQuestions)
    ? exercise.wordJumblesQuestions.map((q) => ({
        jumbled:
          typeof q.jumbled === "string" ? q.jumbled.trim() : "",
        word: Array.isArray(q.word)
          ? q.word.map((w) => String(w).trim()).filter(Boolean)
          : [],
        requireCapitalization: Boolean(q.requireCapitalization),
      }))
    : [];

  /* ================= STUDENT STATE ================= */
  const [state, setState] = useState({
    answers: {}, // { [qIndex]: string }
    hasInteracted: false,
  });

  const completedRef = useRef(false);

  /* ================= RESTORE ATTEMPT ================= */
  useEffect(() => {
    if (!isPreview || !exercise.attempt) return;

    setState({
      answers:
        typeof exercise.attempt.answers === "object"
          ? exercise.attempt.answers
          : {},
      hasInteracted: !!exercise.attempt.hasInteracted,
    });
  }, [isPreview, exercise.attempt]);

  /* ================= RESET ON CHANGE ================= */
  useEffect(() => {
    if (isPreview) completedRef.current = false;
  }, [exercise._id, isPreview]);

  /* ================= NORMALIZE ================= */
  const normalize = (text = "", requireCap = false) => {
    let t = String(text).trim();
    if (!requireCap) t = t.toLowerCase();
    return t.replace(/[.,!?]/g, "").replace(/\s+/g, " ");
  };

  /* ================= EDIT MODE HELPERS ================= */
  const updateQuestions = (next) => {
    if (isEditMode) onUpdate("wordJumblesQuestions", next);
  };

  const handleQuestionChange = (qIdx, field, value) => {
    if (!isEditMode) return;
    const next = [...questions];
    next[qIdx] = {
      ...next[qIdx],
      [field]: typeof value === "string" ? value.trim() : value,
    };
    updateQuestions(next);
  };

  const handleWordArrayChange = (qIdx, value) => {
    if (!isEditMode) return;
    const next = [...questions];
    next[qIdx] = {
      ...next[qIdx],
      word: String(value)
        .split(",")
        .map((v) => v.trim())
        .filter(Boolean),
    };
    updateQuestions(next);
  };

  const handleCapitalizationChange = (qIdx, checked) => {
    if (!isEditMode) return;
    const next = [...questions];
    next[qIdx] = {
      ...next[qIdx],
      requireCapitalization: Boolean(checked),
    };
    updateQuestions(next);
  };

  const handleAddQuestion = () => {
    if (!isEditMode) return;
    updateQuestions([
      ...questions,
      { jumbled: "", word: [], requireCapitalization: false },
    ]);
  };

  const handleRemoveQuestion = (qIdx) => {
    if (!isEditMode) return;
    updateQuestions(questions.filter((_, i) => i !== qIdx));
  };

  /* ================= STUDENT MODE ================= */
  const handleInputChange = (qIdx, value) => {
    if (isEditMode || showResult) return;

    setState((prev) => {
      const nextAnswers = {
        ...prev.answers,
        [qIdx]: value,
      };

      const allAnswered = questions.every(
        (_, i) => nextAnswers[i]?.trim()
      );

      const allCorrect = allAnswered
        ? questions.every((q, i) => {
            const user = normalize(
              nextAnswers[i],
              q.requireCapitalization
            );
            return q.word.some(
              (w) =>
                normalize(w, q.requireCapitalization) === user
            );
          })
        : false;

      const next = {
        ...prev,
        answers: nextAnswers,
        hasInteracted: true,
      };

      setAttempt(next);

      if (allAnswered && !completedRef.current) {
        completedRef.current = true;
        onComplete(exercise._id, allCorrect);
      }

      return next;
    });
  };

  /* ================= EDIT MODE RENDER ================= */
  if (isEditMode) {
    return (
      <div className="space-y-8">
        {questions.length === 0 && (
          <p className="text-sm text-muted-foreground">
            No jumbled words yet. Click “Add Question” to begin.
          </p>
        )}

        {questions.map((q, qIdx) => (
          <Card key={qIdx} className="p-6 space-y-6">
            <StringField
              label={`Jumbled Word ${qIdx + 1}`}
              value={q.jumbled}
              onChange={(v) =>
                handleQuestionChange(qIdx, "jumbled", v)
              }
              placeholder="odnyma"
              required
              error={
                errors?.[
                  `exercise-${exercise?.index ?? qIdx}-wj-jumbled-${qIdx}`
                ]
              }
            />

            <StringField
              label="Correct Answer(s) (comma separated)"
              value={q.word.join(", ")}
              onChange={(v) =>
                handleWordArrayChange(qIdx, v)
              }
              placeholder="Monday"
              required
              error={
                errors?.[
                  `exercise-${exercise?.index ?? qIdx}-wj-correct-${qIdx}`
                ]
              }
            />

            <CapitalizationRule
              checked={q.requireCapitalization}
              onChange={(c) =>
                handleCapitalizationChange(qIdx, c)
              }
            />

            <Button
              variant="destructive"
              size="sm"
              onClick={() => handleRemoveQuestion(qIdx)}
            >
              Remove Question
            </Button>
          </Card>
        ))}

        <Button variant="outline" onClick={handleAddQuestion}>
          Add Question
        </Button>
      </div>
    );
  }

  /* ================= STUDENT VIEW ================= */
  if (questions.length === 0) {
    return (
      <Card className="max-w-2xl mx-auto">
        <CardHeader>
          <CardDescription>
            No word jumble questions available.
          </CardDescription>
        </CardHeader>
      </Card>
    );
  }

  return (
    <Card className="max-w-3xl mx-auto">
      <CardHeader>
        <CardDescription>
          Unscramble each word.
        </CardDescription>
      </CardHeader>

      <CardContent className="space-y-10">
        {questions.map((q, qIdx) => {
          const userAnswer = state.answers[qIdx] || "";
          const normalizedUser = normalize(
            userAnswer,
            q.requireCapitalization
          );
          const correctNormalized = q.word.map((w) =>
            normalize(w, q.requireCapitalization)
          );

          const isCorrect =
            userAnswer && correctNormalized.includes(normalizedUser);

          return (
            <div
              key={qIdx}
              className="p-10 bg-muted rounded-2xl text-center space-y-8"
            >
              <div className="text-5xl font-bold tracking-widest text-blue-700">
                {q.jumbled}
              </div>

              <Input
                value={userAnswer}
                onChange={(e) =>
                  handleInputChange(qIdx, e.target.value)
                }
                placeholder="Type the correct word"
                className={cn(
                  "max-w-md mx-auto text-2xl text-center h-16 font-bold",
                  showResult &&
                    userAnswer &&
                    isCorrect &&
                    "bg-green-100 border-green-600",
                  showResult &&
                    userAnswer &&
                    !isCorrect &&
                    "bg-red-100 border-red-600"
                )}
                disabled={showResult}
              />

              {showResult && userAnswer && (
                <div
                  className={cn(
                    "text-3xl font-bold",
                    isCorrect
                      ? "text-green-700"
                      : "text-red-700"
                  )}
                >
                  {isCorrect
                    ? "Correct!"
                    : `Wrong — Correct: ${q.word.join(" or ")}`}
                </div>
              )}
            </div>
          );
        })}
      </CardContent>
    </Card>
  );
}
